/**
 * Rivers, forests and wilderness in Route Lab. A told feature is shown and played like a
 * narration: the page already knows how to list, map and replay a SimNarration, so each
 * SimFeature is adapted into one, merged in drive order, and marked so the card and the map can
 * tell them apart (a feature has a line or an outline instead of a point, and no verdict).
 */
import { nearestOnLines, type LatLon, type SimFeature, type SimNarration, type SimResult } from '@roadtrip/core';

export type Story = SimNarration & { feature?: SimFeature };

/** The point of the feature nearest where its story began, for the arrow and for framing the map. */
function anchor(f: SimFeature): { lat: number; lon: number } {
  const p = f.startPos;
  const geom = f.feature.geom;
  if (!p) return { lat: geom[0]?.[0]?.[0] ?? 0, lon: geom[0]?.[0]?.[1] ?? 0 };
  let best: LatLon | null = null;
  let bestD = Infinity;
  for (const part of geom) {
    for (const v of part) {
      const d = (v[0] - p.lat) ** 2 + (v[1] - p.lon) ** 2;
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
  }
  return best ? { lat: best[0], lon: best[1] } : { lat: p.lat, lon: p.lon };
}

function asStory(f: SimFeature): Story {
  const ft = f.feature;
  const at = anchor(f);
  const start = f.startPos ?? { t: f.startedAt, lat: at.lat, lon: at.lon };
  const dist = f.startPos ? (nearestOnLines(f.startPos.lat, f.startPos.lon, ft.geom)?.distM ?? 0) : 0;
  return {
    pageId: ft.page_id,
    landmark: {
      page_id: ft.page_id, title: ft.title, lat: at.lat, lon: at.lon, trigger_radius_m: 0,
      headline: ft.headline, script: ft.script, est_seconds: ft.est_seconds, wiki_url: ft.wiki_url,
    },
    intro: `${ft.title}.`,
    triggeredAt: f.queuedAt,
    triggerPos: start,
    startedAt: f.startedAt,
    startPos: f.startPos,
    endedAt: f.endedAt,
    endPos: f.endPos,
    reason: f.reason,
    distAtTrigger: dist,
    distAtStart: dist,
    distAtEnd: null,
    triggerRadius: 0,
    speedMps: null,
    feature: f,
  };
}

/** The result with features folded into its narrations, in the order they were spoken. */
export function withFeatures(r: SimResult): SimResult {
  if (!r.features.length) return r;
  const narrations: Story[] = [...r.narrations, ...r.features.filter((f) => !Number.isNaN(f.startedAt)).map(asStory)];
  narrations.sort((a, b) => a.startedAt - b.startedAt);
  return { ...r, narrations };
}

export const isFeature = (n: SimNarration): n is Story & { feature: SimFeature } => (n as Story).feature != null;
